import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import {useOpenPGP} from './useOpenPGP';
import {FileData} from 'types';
import {useSortingMenu, useSortingType} from 'context/useSorting';
import {API_SERVER, LOCAL_STORE_CONFIG_NAMES, TILE_SIZE_INIT_VALUE} from 'const';
import useLocalStorageState from './useLocalStorageState';
import {CreateFolderContext, MoveContext, useCreateFolderContext} from './useCreateFolderContext';
import {api} from 'api';
import {Msg} from 'components/notifications/NotificationContext';
import {MoveDialog} from 'components/dialogs/MoveDialog';
import {useMove} from './useMove';
import {useAuthContext} from './AuthContext';

const SERVER_API = API_SERVER;

// type ViewSettings = {
//    tileSize: 'small' | 'medium' | 'large';
//    setTileSize: (size: 'small' | 'medium' | 'large') => void;
// };

type ViewSettings = {
   tileSize: number;
   setTileSize: (size: number) => void;
};

interface FileContextType extends ViewSettings {
   currentFolder: FileData | null;
   loadFolder: (path: string) => Promise<void>;
   refetchFolder: () => Promise<void>;
   setCurrentFolder: (folder: FileData | null) => void;
   viewMode: 'table' | 'grid';
   setViewMode: (mode: 'table' | 'grid') => void;

   publicArmoredKey: string;
   downloadEncrypted: (path: string) => void;

   selectedFiles: FileData[];
   setSelectedFiles: (files: FileData[]) => void;

   sort: useSortingType;
   create: CreateFolderContext;
   remove: (file: FileData) => void;
   removeSelected: () => void;
   move: MoveContext;
}

const FileContext = createContext<FileContextType | undefined>(undefined);

const fetchFolders = async (path: string) => {
   try {
      const response = await axios.get(`${api.files}?path=${path}`);
      const children = response.data.map((f: FileData) => ({
         ...f,
         id: f.id || f.name,
         createdAt: Number(f.createdAt),
         modifiedAt: Number(f.modifiedAt),
         size: Number(f.size),
      }));

      return children;
   } catch (error) {
      Msg.notify('Failed to load folder: ' + (error as Error).message, 'error');
      console.error('Failed to load folder:', error);
   }
};

export const FileProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
   const [currentFolder, setCurrentFolder] = useState<FileData | null>(null);
   const {publicArmoredKey, downloadEncrypted} = useOpenPGP();
   const [viewMode, setViewMode] = useLocalStorageState<'table' | 'grid'>(
      LOCAL_STORE_CONFIG_NAMES.view,
      'table'
   ); // Default to table view
   const [tileSize, setTileSize] = useLocalStorageState<number>(
      LOCAL_STORE_CONFIG_NAMES.tileSize,
      TILE_SIZE_INIT_VALUE
   );

   const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);

   const sort = useSortingMenu(currentFolder?.children);

   const loadFolder = useCallback(
      async (path: string) => {
         try {
            const children = await fetchFolders(path);
            setCurrentFolder({path, children} as FileData);
            setSelectedFiles([]);
         } catch (error) {
            // Msg.notify('Failed to load folder: ' + (error as Error).message, 'error');
            console.error('Failed to load folder:', error);
         }
      },
      [setCurrentFolder]
   );

   const {
      authData: {homeDirectory},
   } = useAuthContext();

   useEffect(() => {
      console.debug('load folder', homeDirectory);
      loadFolder(homeDirectory || '/');
   }, [loadFolder, homeDirectory]);

   const refetchFolder = useCallback(async () => {
      await loadFolder(currentFolder!.path);
   }, [loadFolder, currentFolder?.path]);

   const create = useCreateFolderContext(setCurrentFolder, refetchFolder, currentFolder);

   const move = useMove(selectedFiles, refetchFolder);
   const {move: _move, moveOpened, closeMove, currentMovePath} = move;

   const remove = useCallback(
      async (file: FileData) => {
         try {
            await axios.delete(`${api.remove}`, {data: {path: file.path}});

            Msg.notify(`${file.name} has been removed.`, 'success');

            refetchFolder();
         } catch (e) {
            console.debug(e);
            Msg.notify('Failed to remove: ' + (e as Error).message, 'error');
         }
      },
      [refetchFolder]
   );

   const removeSelected = useCallback(async () => {
      try {
         for (let file of selectedFiles) {
            await axios.delete(`${api.remove}`, {data: {path: file.path}});
         }

         Msg.notify(`Selected items(${selectedFiles.length}) have been removed.`, 'success');
         refetchFolder();
      } catch (e) {
         console.debug(e);
         Msg.notify('Failed to remove selected items: ' + (e as Error).message, 'error');
      }
   }, [selectedFiles]);

   return (
      <FileContext.Provider
         value={{
            currentFolder,
            loadFolder,
            refetchFolder,
            setCurrentFolder,
            viewMode,
            setViewMode,
            publicArmoredKey,
            downloadEncrypted,
            selectedFiles,
            setSelectedFiles,
            sort,
            create,
            tileSize,
            setTileSize,
            remove,
            removeSelected,
            move,
         }}
      >
         {children}
         {create.RenameDialogComponent}
         {moveOpened && (
            <MoveDialog
               open={true}
               onClose={closeMove}
               fetchFolder={fetchFolders}
               currentPath={currentMovePath}
               onSelect={_move}
            />
         )}
      </FileContext.Provider>
   );
};

export const useFileContext = () => {
   const context = useContext(FileContext);
   if (!context) {
      throw new Error('useFileContext must be used within a FileProvider');
   }
   return context;
};
